import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import type { Application, ApplicationInsert, ApplicationUpdate } from '../types/database'

interface UseApplicationsReturn {
  applications: Application[]
  loading: boolean
  error: string | null
  createApplication: (data: ApplicationInsert) => Promise<Application | null>
  updateApplication: (id: string, data: ApplicationUpdate) => Promise<boolean>
  deleteApplication: (id: string) => Promise<boolean>
  regenerateSecret: (id: string) => Promise<string | null>
  refresh: () => Promise<void>
}

export function useApplications(): UseApplicationsReturn {
  const { user } = useAuth()
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadApplications = useCallback(async () => {
    if (!user) {
      setApplications([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setApplications(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  const generateAppKey = () => {
    const bytes = new Uint8Array(24)
    crypto.getRandomValues(bytes)
    return 'wba_' + Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  const generateAppSecret = () => {
    const bytes = new Uint8Array(48)
    crypto.getRandomValues(bytes)
    return 'wbs_' + Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  const createApplication = async (data: ApplicationInsert): Promise<Application | null> => {
    if (!user) return null

    try {
      const newApp = {
        ...data,
        owner_id: user.id,
        app_key: generateAppKey(),
        app_secret: generateAppSecret()
      }

      const { data: created, error: createError } = await supabase
        .from('applications')
        .insert(newApp)
        .select()
        .single()

      if (createError) throw createError

      setApplications(prev => [created, ...prev])
      return created
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create application')
      return null
    }
  }

  const updateApplication = async (id: string, data: ApplicationUpdate): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('applications')
        .update(data)
        .eq('id', id)

      if (updateError) throw updateError

      setApplications(prev =>
        prev.map(app => app.id === id ? { ...app, ...data } : app)
      )
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update application')
      return false
    }
  }

  const deleteApplication = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('applications')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      setApplications(prev => prev.filter(app => app.id !== id))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete application')
      return false
    }
  }

  const regenerateSecret = async (id: string): Promise<string | null> => {
    try {
      const newSecret = generateAppSecret()

      const { error: updateError } = await supabase
        .from('applications')
        .update({ app_secret: newSecret })
        .eq('id', id)

      if (updateError) throw updateError

      setApplications(prev =>
        prev.map(app => app.id === id ? { ...app, app_secret: newSecret } : app)
      )
      return newSecret
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate secret')
      return null
    }
  }

  return {
    applications,
    loading,
    error,
    createApplication,
    updateApplication,
    deleteApplication,
    regenerateSecret,
    refresh: loadApplications
  }
}
